/**
 * The project register as spreadsheet rows.
 *
 * React-free and fetch-free on purpose, like `lib.ts` beside it: what belongs in
 * a column and in what form is the part of the export that can be wrong without
 * anything on screen looking wrong, so it has to be readable and testable
 * without mounting a component or answering a request.
 *
 * ── WHAT GOES OUT AS A NUMBER, AND WHY IT MATTERS ──────────────────────────
 *
 * Money leaves as EUROS AS A NUMBER, never as the string the screen prints. The
 * register shows "12 400 €", and a spreadsheet cannot add that - it lands as
 * text, the SUM at the bottom of the column reads 0, and the person who asked
 * for the file has no way to tell that from a set of projects with no budgets.
 * `lib/csv.ts` exempts numbers from its formula guard for the same reason,
 * which its header argues at length.
 *
 * An ABSENT budget leaves as an empty cell, not as 0. Zero is a budget somebody
 * agreed to; empty is a decision nobody has made yet, and a column of zeros
 * would drag the average down with figures that were never numbers.
 *
 * Dates leave as `YYYY-MM-DD`, sliced off the ISO instant rather than formatted.
 * That is the one date form every spreadsheet in every locale parses as a date;
 * the operator's own `31.12.2026` is read as text by a sheet set to any other
 * locale, and sorting a column of those sorts them by their first digit.
 *
 * ── THE TWO COLUMNS THAT ARE NOT ON THE SCREEN'S LIST ──────────────────────
 *
 * SPENT IS NOT HERE, and its absence is deliberate rather than forgotten. It is
 * not a column of the register: `spentCents` exists only on the per-project
 * summary endpoint, one project at a time, and the list row genuinely does not
 * carry it (`ProjectsListRow` says the same thing about drawing it). The two
 * ways to put it in this file are both worse than leaving it out - one request
 * per row turns a 300-project export into 300 requests, and an empty column
 * headed "Spent" tells an accountant that nothing has been spent. If it is ever
 * wanted, the honest route is a server-side export that computes it in one
 * query, not this function growing a fetch.
 *
 * ARCHIVED IS HERE even though it is blank on every row of the daily register.
 * The export follows whatever archive filter the register is under, so a file
 * can legitimately mix live and archived projects, and one that did with no way
 * to tell them apart would be exactly the quiet lie this export exists to
 * avoid. A stable header row also survives being pasted next to last month's.
 */
import type { CsvCell } from "../../../lib/csv";
import type { Project } from "../../../lib/projects";

/**
 * The header row, as catalogue keys.
 *
 * Keys and not words, so the file comes out in the language the operator is
 * reading the register in - the same rule the rest of the CRM follows. The order
 * here IS the column order of `projectCsvRow` below; the two are read side by
 * side on purpose, because a header and a row that disagree by one column is a
 * spreadsheet in which every figure is filed under the heading to its left.
 */
export const PROJECT_CSV_HEADER_KEYS = [
  "projects.col.name",
  "projects.client",
  "projects.statusCol",
  "projects.progress",
  "projects.budget",
  "projects.deadline",
  "projects.tags",
  "projects.members",
  "projects.archived",
] as const;

export interface ProjectCsvContext {
  /** The catalogue, for the header and for the status word. */
  t: (key: string) => string;
  /** An id from `project.members` to a name a human recognises. Resolved by the
   *  caller, because the directory is a hook and this file holds no React. */
  memberName: (id: string) => string;
}

/** One project, in the order `PROJECT_CSV_HEADER_KEYS` names. */
export function projectCsvRow(p: Project, ctx: ProjectCsvContext): CsvCell[] {
  return [
    p.name,
    p.client?.name ?? "",
    ctx.t(`projects.status.${p.status}`),
    // The EFFECTIVE percentage, with the same fallback the row on screen uses.
    // On a `progressSource: "tasks"` project the stored column still holds
    // whatever a person last typed, and exporting that would put a third number
    // beside the register and the client's portal - the file and the screen have
    // to agree, or the export is a second opinion nobody asked for.
    p.effectiveProgressPct ?? p.progressPct,
    p.budgetCents == null ? "" : p.budgetCents / 100,
    p.deadline ? p.deadline.slice(0, 10) : "",
    // Both lists go into ONE cell each. A column per tag would have to be as
    // wide as the busiest project in the set, which changes with the filter, and
    // a file whose shape depends on its contents cannot be pasted under last
    // month's. `toCsv` quotes any cell holding the separator, so the joiner is
    // free to be the one that reads as a list to a person.
    p.tags.join(", "),
    p.members.map(ctx.memberName).join(", "),
    p.archivedAt ? p.archivedAt.slice(0, 10) : "",
  ];
}

/** The header row plus one row per project, ready for `toCsv`/`downloadCsv`. */
export function projectCsvRows(rows: readonly Project[], ctx: ProjectCsvContext): CsvCell[][] {
  return [
    PROJECT_CSV_HEADER_KEYS.map((k) => ctx.t(k)),
    ...rows.map((p) => projectCsvRow(p, ctx)),
  ];
}
